import React, { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Button, Select, SelectOption, Tree, TreeNode } from '@freelancelabs/components';

import { ListSkeleton } from '../../../components/Skeletons';
import { DataScope, MembershipStatus, OrganizationKind } from '../../../graphql/__generated__/schema';
import {
    AccessibleAccountsDocument,
    AccessibleAccountsQuery,
    ContactsByEmailDocument,
    RolesDocument,
} from '../../../graphql/__generated__/types';
import PortalAccessDialog from '../../accounts/PortalAccessDialog';
import AttachmentRow from './AttachmentRow';
import { Draft, DraftAttachment } from './draft';
import { refusalRules } from './grantableRoles';

interface Props {
    draft: Draft;
    onChange: (patch: Partial<Draft>) => void;
    onEditIdentity: () => void;
}

type Account = NonNullable<NonNullable<AccessibleAccountsQuery['accessibleAccounts']>[number]>;

/**
 * Step 2: the accounts, and what the person carries on each (role and perimeter per account).
 *
 * A person belongs to a single root organisation, so only one company's accounts are listed and
 * the others are hidden. The company is anchored on the contact, not on the first tick.
 */
const AttachmentsStep = ({ draft, onChange, onEditIdentity }: Props) => {
    const [opening, setOpening] = useState<Account | null>(null);
    const [toOpenId, setToOpenId] = useState<string | null>(null);

    const {
        data: accountsData,
        loading: accountsLoading,
        error: accountsError,
        refetch: refetchAccounts,
    } = useQuery(AccessibleAccountsDocument);
    const allAccounts = (accountsData?.accessibleAccounts ?? []).filter(Boolean);
    const accounts = allAccounts.filter(
        (account): account is Account => Boolean(account.id) && account.kind !== OrganizationKind.Internal
    );

    const email = draft.email.trim();

    // The step 1 answer, re-read here for two facts: existingAttachments greys (the person
    // already has portal access), contact.accounts pre-ticks (the source). When both, greyed wins.
    const {
        data: contactData,
        loading: contactLoading,
        error: contactError,
    } = useQuery(ContactsByEmailDocument, {
        variables: { email },
        skip: email.length === 0,
    });
    const attachedStatusById = new Map(
        (contactData?.contactsByEmail?.existingAttachments ?? []).map((attachment) => [
            attachment.organizationId,
            attachment.status,
        ])
    );

    const { data: rolesData, error: rolesError, refetch: refetchRoles } = useQuery(RolesDocument);
    // Portal scope only: a back-office role on a client account would give the person
    // the administration screens.
    const roles = (rolesData?.portal ?? []).filter(Boolean);

    const selectedIds = draft.attachments.map((attachment) => attachment.organizationId);
    const byId = new Map(accounts.map((account) => [account.id as string, account]));

    // The single picked contact, never all contacts of a shared mailbox.
    const pickedContactId = draft.contact?.id;
    const pickedContact =
        contactData?.contactsByEmail?.contacts.find((contact) => contact.id === pickedContactId) ?? null;
    const sourceAccounts = pickedContactId ? (pickedContact?.accounts ?? []) : [];
    const sourceById = new Map(sourceAccounts.map((account) => [account.id, account]));

    const designated = sourceAccounts
        .map((source) => byId.get(source.id))
        .filter((account): account is Account => Boolean(account));

    // The company all accounts must belong to, or null. Anchored on the contact first, ticks only
    // failing that. A null root does not anchor, and several roots anchor nothing.
    const designatedRoots = [...new Set(designated.map((account) => account.rootOrganizationId).filter(Boolean))];
    const acrossCompanies = designatedRoots.length > 1;
    const contactAnchor =
        designatedRoots.length === 1
            ? (designated.find((account) => account.rootOrganizationId === designatedRoots[0]) ?? null)
            : null;
    const anchor =
        contactAnchor ?? selectedIds.map((id) => byId.get(id)).find((account) => account?.rootOrganizationId);
    const lockedRootId = anchor?.rootOrganizationId ?? null;
    const lockedRootName = anchor?.rootOrganizationName ?? null;

    // Refused only when we can prove it belongs to another company; a null root is not that proof.
    const isRefused = (account: Account) =>
        Boolean(lockedRootId && account.rootOrganizationId && account.rootOrganizationId !== lockedRootId);

    // Only false is proof of a closed account; a null means the API did not say, so it stays pickable.
    const isClosedToPortal = (account: Account) => account.portalEnabled === false;

    // Accounts whose only block is a closed portal, minus already-attached ones (opening a
    // portal for someone already attached is a no-op) and accounts of another company.
    const closedAccounts = accounts.filter(
        (account) => isClosedToPortal(account) && !isRefused(account) && !attachedStatusById.has(account.id as string)
    );

    const closedOptions: SelectOption<string>[] = closedAccounts.map((account) => ({
        label: account.name ?? '',
        value: account.id as string,
    }));
    const toOpen = closedAccounts.find((account) => account.id === toOpenId) ?? null;

    // All four statuses refuse the invitation, but they call different gestures: REVOKED/LEFT
    // need a reopen where INVITED/ACTIVE are settled from the person's sheet.
    const attachmentOf = (account: Account) => attachedStatusById.get(account.id as string);
    const isAlreadyAttached = (account: Account) => attachmentOf(account) !== undefined;

    const refusalOf = (account: Account) => {
        const status = attachmentOf(account);
        if (status === MembershipStatus.Revoked || status === MembershipStatus.Left) {
            return c('Onboarding').t`Access to reopen`;
        }
        if (status !== undefined) {
            return c('Onboarding').t`Already attached`;
        }
        if (isRefused(account)) {
            return c('Onboarding').t`Another company`;
        }
        if (isClosedToPortal(account)) {
            return c('Onboarding').t`Not open to the portal`;
        }
        return undefined;
    };

    // Accounts of other companies are HIDDEN, not greyed (the one place this screen departs from
    // "disabled with its reason"): the refusal is total and would drown the tree. Only hide when the
    // anchor is known, and never hide a ticked or already-attached row.
    const isTicked = (account: Account) => selectedIds.includes(account.id as string);
    const isHidden = (account: Account) => isRefused(account) && !isTicked(account) && !isAlreadyAttached(account);

    // A ticked account stays enabled even when impossible, else a corrected address could leave it
    // ticked, disabled and un-untickable. These ids are shown ticked but never added to the draft.
    const tickedInTree = [...selectedIds, ...attachedStatusById.keys()];

    const toNode = (account: Account, level: string): TreeNode => {
        const source = sourceById.get(account.id as string);
        return {
            id: account.id as string,
            label: account.name ?? '',
            badges: [
                level,
                ...(source
                    ? [
                          c('Onboarding account origin').t`Designated by the source`,
                          ...(source.isPrimary ? [c('Onboarding account origin').t`Main company`] : []),
                      ]
                    : []),
            ],
            disabled:
                !isTicked(account) && (isAlreadyAttached(account) || isRefused(account) || isClosedToPortal(account)),
            disabledReason: refusalOf(account),
        };
    };

    // Grouped by company, depth capped at two: accessibleAccounts is a perimeter, not a tree, so it
    // carries the root but not the parent. Hiding happens only here; every other calc uses all accounts.
    const buildNodes = (): TreeNode[] => {
        const groups = new Map<string, Account[]>();
        const rootless: Account[] = [];

        accounts
            .filter((account) => !isHidden(account))
            .forEach((account) => {
                if (!account.rootOrganizationId) {
                    rootless.push(account);
                    return;
                }
                const members = groups.get(account.rootOrganizationId) ?? [];
                members.push(account);
                groups.set(account.rootOrganizationId, members);
            });

        const grouped = [...groups.entries()]
            .map(([rootId, members]) => {
                const rootAccount = members.find((account) => account.id === rootId);
                const children = members
                    .filter((account) => account.id !== rootId)
                    .map((account) => toNode(account, c('Onboarding account level').t`Child account`));
                const rootName = members[0].rootOrganizationName ?? c('Onboarding').t`Company without a name`;

                if (rootAccount) {
                    return {
                        ...toNode(rootAccount, c('Onboarding account level').t`Master account`),
                        children,
                    };
                }
                return {
                    id: `group-${rootId}`,
                    label: rootName,
                    selectable: false,
                    children,
                };
            })
            .sort((left, right) => String(left.label).localeCompare(String(right.label)));

        return [
            ...grouped,
            // No company at all: kept last, named for what it is.
            ...rootless.map((account) => toNode(account, c('Onboarding account level').t`Company unknown`)),
        ];
    };

    // Why the tree is empty. A failed or in-flight query also leaves it empty; neither means "no account".
    const emptyReason = (): string | null => {
        if (accountsError) {
            return c('Onboarding').t`The accounts could not be loaded.`;
        }
        if (accountsLoading) {
            return c('Onboarding').t`Loading the accounts…`;
        }
        if (allAccounts.length === 0) {
            return c('Onboarding').t`No account you can act on.`;
        }
        if (accounts.length === 0) {
            return c('Onboarding')
                .t`The only organisation you can act on is Freelance.com, which is not a client account. Onboarding a colleague goes through the collaborator journey.`;
        }
        return null;
    };

    const accountName = (organizationId: string) => byId.get(organizationId)?.name ?? organizationId;

    const newAttachment = (organizationId: string): DraftAttachment => ({
        organizationId,
        roleId: null,
        // Pre-selected on screen, a choice made visibly, since the API takes no default.
        dataScope: DataScope.All,
        needIds: [],
        serviceIds: [],
        suggestion: null,
    });

    const addAccounts = (organizationIds: string[]) =>
        onChange({ attachments: [...draft.attachments, ...organizationIds.map(newAttachment)] });

    // Pre-ticking, applied once per address. The snapshot is written even when nothing is ticked, to
    // tell "ran and found nothing" from "not run yet". No deps array: everything read is rebuilt each render.
    useEffect(() => {
        if (accountsLoading || contactLoading || accountsError || contactError) {
            return;
        }
        if (!pickedContact || draft.accountSuggestion?.email === email) {
            return;
        }
        const preTicked = acrossCompanies
            ? []
            : designated.filter(
                  (account) =>
                      !isAlreadyAttached(account) &&
                      !isClosedToPortal(account) &&
                      !isRefused(account) &&
                      !selectedIds.includes(account.id as string)
              );
        const accountIds = preTicked.map((account) => account.id as string);
        onChange({
            attachments: [...draft.attachments, ...accountIds.map(newAttachment)],
            accountSuggestion: { email, accountIds },
        });
    });

    const patchAttachment = (organizationId: string, patch: Partial<DraftAttachment>) =>
        onChange({
            attachments: draft.attachments.map((attachment) =>
                attachment.organizationId === organizationId ? { ...attachment, ...patch } : attachment
            ),
        });

    // Dropping an account drops the dashboards that hung from it: inviteUser would refuse them out of scope.
    const removeAttachment = (organizationId: string) =>
        onChange({
            attachments: draft.attachments.filter((attachment) => attachment.organizationId !== organizationId),
            dashboards: draft.dashboards.filter((dashboard) => dashboard.organizationId !== organizationId),
        });

    const count = draft.attachments.length;

    // Read from the snapshot, in the past tense, so it stays true after the operator unticks everything.
    const applied = draft.accountSuggestion?.email === email ? draft.accountSuggestion : null;
    const preTickedCount = applied?.accountIds.length ?? 0;

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h2 className="text-sm font-semibold text-text-900">{c('Onboarding').t`Account selection`}</h2>
            </div>

            {accountsError && (
                <div className="flex flex-wrap items-center gap-3 text-sm text-danger-600" role="alert">
                    <span>
                        {c('Onboarding')
                            .t`The accounts could not be loaded. The tree below is empty for want of an answer, not for want of accounts.`}
                    </span>
                    <Button category="weak" size="small" onClick={() => refetchAccounts()}>
                        {c('Action').t`Retry`}
                    </Button>
                </div>
            )}

            {preTickedCount > 0 && (
                <p
                    className="rounded-lg border border-admin-accent-border bg-admin-accent-soft px-3 py-2 text-sm text-text-600"
                    role="status"
                >
                    {c('Onboarding suggestion').ngettext(
                        msgid`${preTickedCount} account was pre-ticked to start with, because the source attaches this person to it. Untick whatever should not be granted, and tick whatever the source does not name: it says where the person works, not what you decide to open.`,
                        `${preTickedCount} accounts were pre-ticked to start with, because the source attaches this person to them. Untick whatever should not be granted, and tick whatever the source does not name: it says where the person works, not what you decide to open.`,
                        preTickedCount
                    )}
                </p>
            )}

            {acrossCompanies && (
                <p
                    className="rounded-lg border border-admin-accent-border bg-admin-accent-soft px-3 py-2 text-sm text-warning-700"
                    role="alert"
                >
                    {c('Onboarding suggestion')
                        .t`The source attaches this person to accounts of several companies, so nothing was pre-ticked and every company is listed below: client-side a person belongs to a single one, and the invitation would refuse the lot. Tick the accounts of the right company, and the others leave the list.`}
                </p>
            )}

            {/* The hiding rule, said once. Two sentences: a contact anchor does not undo by
                unticking, a first-tick anchor does. */}
            {lockedRootName && (
                <div
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-admin-accent-border bg-admin-accent-soft px-3 py-2 text-sm text-text-600"
                    role="status"
                >
                    {contactAnchor ? (
                        <>
                            <span>
                                {c('Onboarding')
                                    .t`Only the accounts of ${lockedRootName} are listed: this is the company the source attaches this person to, and client-side a person belongs to a single one, so the other companies are not shown here. If it is not the right company, it is the contact that has to change, not the boxes below.`}
                            </span>
                            <Button category="weak" size="small" onClick={onEditIdentity}>
                                {c('Onboarding action').t`Check the address`}
                            </Button>
                        </>
                    ) : (
                        <span>
                            {c('Onboarding')
                                .t`Only the accounts of ${lockedRootName} are listed, the first tick having settled the company: client-side, a person belongs to a single one. Untick every account to see the whole tree again.`}
                        </span>
                    )}
                </div>
            )}

            {!lockedRootName && !acrossCompanies && pickedContact && !accountsLoading && !accountsError && (
                <p
                    className="rounded-lg border border-admin-accent-border bg-admin-accent-soft px-3 py-2 text-sm text-text-600"
                    role="status"
                >
                    {c('Onboarding')
                        .t`The source names no company for this person among the accounts you administer, so all of them are listed. The first account ticked settles the company, and the others then leave the list: client-side, a person belongs to a single company.`}
                </p>
            )}

            <div className="max-h-80 overflow-auto rounded-lg border border-border-norm px-2 py-2">
                {/* Skeleton replaces the tree, not emptyLabel: loading is not the same as "nothing". */}
                {accountsLoading ? (
                    <ListSkeleton rows={7} withControl />
                ) : (
                    <Tree
                        nodes={buildNodes()}
                        selected={tickedInTree}
                        onToggle={(id, checked) => (checked ? addAccounts([id]) : removeAttachment(id))}
                        onSelectBranch={addAccounts}
                        branchActionLabel={c('Onboarding action').t`Tick its child accounts too`}
                        label={c('Onboarding').t`Account tree`}
                        emptyLabel={emptyReason()}
                    />
                )}
            </div>

            {/* The remedy under the tree, not a control inside a disabled row. Opens in place
                to keep the draft, which lives in component state. */}
            {closedAccounts.length > 0 && (
                <div className="rounded-lg border border-border-norm bg-background-weak px-3 py-2.5" role="status">
                    {/* One button and a target to pick, not one button per account. */}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Select
                            options={closedOptions}
                            value={closedOptions.find((option) => option.value === toOpenId)}
                            onChange={(option) => setToOpenId(option.value)}
                            placeholder={c('Onboarding').t`Pick an account to open`}
                            aria-label={c('Onboarding').t`Account to open to the portal`}
                            className="max-w-xs"
                        />
                        <Button
                            category="weak"
                            size="small"
                            disabled={!toOpen}
                            onClick={() => toOpen && setOpening(toOpen)}
                        >
                            {c('Onboarding action').t`Open this account to the portal…`}
                        </Button>
                    </div>
                </div>
            )}

            {opening?.id && (
                <PortalAccessDialog
                    accountId={opening.id}
                    accountName={opening.name ?? opening.id}
                    direction="open"
                    onClose={() => {
                        setOpening(null);
                        // A stale id would leave the select showing a value it no longer offers.
                        setToOpenId(null);
                    }}
                />
            )}

            <p className="text-xs text-text-600">
                {ngettext(msgid`${count} account selected.`, `${count} accounts selected.`, count)}
            </p>

            {rolesError && (
                <div className="flex flex-wrap items-center gap-3 text-sm text-danger-600" role="alert">
                    <span>{c('Onboarding').t`The roles could not be loaded, so no role can be picked below.`}</span>
                    <Button category="weak" size="small" onClick={() => refetchRoles()}>
                        {c('Action').t`Retry`}
                    </Button>
                </div>
            )}

            {/* Why some rows refuse: said once, above, not per row. */}
            {refusalRules(roles).map((rule) => (
                <p
                    key={rule}
                    className="rounded-lg border border-admin-accent-border bg-admin-accent-soft px-3 py-2 text-sm text-text-600"
                    role="status"
                >
                    {rule}
                </p>
            ))}

            {draft.attachments.length === 0 ? (
                // Silent when the query failed: the alert above already says what is going on.
                !accountsError && (
                    <p className="rounded-lg border border-dashed border-border-norm px-4 py-6 text-center text-xs text-text-600">
                        {c('Onboarding')
                            .t`No account yet. A person without an attachment would sign in and see nothing.`}
                    </p>
                )
            ) : (
                <ul className="flex flex-col gap-3">
                    {draft.attachments.map((attachment) => (
                        <AttachmentRow
                            key={attachment.organizationId}
                            attachment={attachment}
                            accountName={accountName(attachment.organizationId)}
                            email={draft.email.trim()}
                            roles={roles}
                            onEditIdentity={onEditIdentity}
                            onChange={(patch) => patchAttachment(attachment.organizationId, patch)}
                            onRemove={() => removeAttachment(attachment.organizationId)}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AttachmentsStep;
